import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type {
  VendorProfile,
  AuthStatus,
  AuthStep,
  VendorLoginRequest,
  VendorRegisterRequest,
  CompleteProfileRequest,
  UpdateProfileRequest,
  ChangePasswordRequest,
} from '@cravo/core'
import { vendorAuthApi, tokenManager, configureApi } from '@cravo/api-service'
import { initFirebase, signInWithGoogle, signInWithApple, sendPhoneOtp, verifyPhoneOtp, signOutFirebase } from './firebase'

/**
 * Vendor-app device id. The `web-vendor-` prefix is read by the backend's
 * sendPushToTenantUser to route per-app notifications: a VENDOR_NEW_ORDER
 * push fires only to tokens whose deviceId starts with `web-vendor-`,
 * ORDER_PLACED only to `web-customer-`, etc. Without this tagging the
 * backend can't tell which of a user's WEB tokens belongs to which app
 * (same user often has customer + vendor + rider tokens against the
 * same userId).
 *
 * Legacy IDs (`web-<uuid>` with no app segment) are auto-upgraded — we
 * tag them with the current app and keep the UUID for session continuity.
 */
export function getDeviceId(): string {
  const key = 'cravo-device-id'
  const PREFIX = 'web-vendor-'
  let id = localStorage.getItem(key)
  if (id && !id.startsWith(PREFIX)) {
    const tail = id.startsWith('web-') ? id.slice('web-'.length) : id
    id = PREFIX + tail
    localStorage.setItem(key, id)
  }
  if (!id) {
    id = PREFIX + crypto.randomUUID()
    localStorage.setItem(key, id)
  }
  return id
}

function getDeviceInfo() {
  return {
    deviceId: getDeviceId(),
    deviceName: navigator.userAgent.split('(')[1]?.split(')')[0] || 'Web Browser',
    deviceModel: `Web/${navigator.platform || 'Unknown'}`,
  }
}

function isSyntheticPhone(phone?: string | null) {
  if (!phone) return true
  return phone.startsWith('+firebase-') || phone.startsWith('+placeholder-')
}

/**
 * Backend creates `<random>@firebase.local` placeholder emails for phone-only
 * social-login signups. These should NOT count as a real email — owner
 * registration screen must be shown until vendor adds a real one.
 */
function isPlaceholderEmail(email?: string | null) {
  if (!email) return true
  return email.endsWith('@firebase.local')
}

function normalizeVendorProfile(raw: unknown): VendorProfile | null {
  if (!raw || typeof raw !== 'object') return null
  const source = raw as Record<string, unknown>
  const name = typeof source.fullName === 'string'
    ? source.fullName
    : typeof source.name === 'string'
      ? source.name
      : ''
  const phone = typeof source.phone === 'string' ? source.phone : ''
  const email = typeof source.email === 'string' ? source.email : ''
  const businesses = Array.isArray(source.businesses) ? source.businesses : []

  if (typeof source.id !== 'string') return null

  return {
    id: source.id,
    fullName: name,
    email,
    phone,
    profilePicture: typeof source.profilePicture === 'string' ? source.profilePicture : null,
    emailVerified: Boolean(source.emailVerified),
    phoneVerified: Boolean(source.phoneVerified),
    // Treat profileComplete as true ONLY when name + real phone + real email
    // are all present. Backend may flag profileComplete=true for phone-only
    // signups (which still have @firebase.local placeholder email) — we
    // tighten the check client-side to keep parity with mobile owner-reg gating.
    profileComplete: typeof source.profileComplete === 'boolean'
      ? source.profileComplete &&
        Boolean(name.trim()) &&
        !isSyntheticPhone(phone) &&
        !isPlaceholderEmail(email)
      : Boolean(name.trim()) &&
        !isSyntheticPhone(phone) &&
        !isPlaceholderEmail(email),
    businesses: businesses as VendorProfile['businesses'],
  }
}

function getNextStep(user: VendorProfile): AuthStep {
  // Force owner registration when ANY of: name missing, synthetic phone,
  // placeholder email, profileComplete=false. Mobile parity with VendorStatus
  // unverified / verifiedNoBusiness routing.
  const hasName = Boolean(user.fullName?.trim())
  const hasRealPhone = !isSyntheticPhone(user.phone)
  const hasRealEmail = !isPlaceholderEmail(user.email)
  if (!user.profileComplete || !hasName || !hasRealPhone || !hasRealEmail) {
    return 'profile_completion'
  }
  return 'idle'
}

async function resolveAuthenticatedVendor(user?: unknown): Promise<VendorProfile> {
  const normalized = normalizeVendorProfile(user)
  if (normalized) return normalized
  const me = await vendorAuthApi.me()
  const hydrated = normalizeVendorProfile((me as { user?: unknown; vendor?: unknown }).user ?? (me as { vendor?: unknown }).vendor ?? me)
  if (!hydrated) {
    throw new Error('Authenticated user details could not be loaded.')
  }
  return hydrated
}

// ─── Context Type ─────────────────────────────────────────────────

export interface VendorAuthContextValue {
  vendor: VendorProfile | null
  status: AuthStatus
  step: AuthStep
  pendingEmail: string | null
  pendingPhone: string | null
  resetToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  /** True iff a Firebase app initialized successfully for this tenant.
   *  When false, social-login + Firebase phone OTP are unavailable. */
  firebaseEnabled: boolean

  register: (data: VendorRegisterRequest) => Promise<void>
  login: (data: VendorLoginRequest) => Promise<void>
  loginWithGoogle: () => Promise<void>
  loginWithApple: () => Promise<void>
  sendPhoneLoginOtp: (phone: string, recaptchaId: string) => Promise<void>
  verifyPhoneLoginOtp: (otp: string) => Promise<void>
  completeProfile: (data: CompleteProfileRequest) => Promise<void>
  verifyOtp: (otp: string) => Promise<void>
  resendOtp: () => Promise<void>
  logout: () => Promise<void>

  forgotPassword: (email: string) => Promise<void>
  verifyForgotPasswordOtp: (otp: string) => Promise<void>
  resetPassword: (newPassword: string) => Promise<void>
  changePassword: (data: ChangePasswordRequest) => Promise<void>

  updateProfile: (data: UpdateProfileRequest) => Promise<VendorProfile>
  refreshVendor: () => Promise<void>
  checkAvailability: (email?: string, phone?: string) => Promise<{ emailAvailable?: boolean; phoneAvailable?: boolean }>
  setStep: (step: AuthStep) => void
}

export const VendorAuthContext = createContext<VendorAuthContextValue | null>(null)

// ─── Provider Props ───────────────────────────────────────────────

interface FirebaseConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
}

interface VendorAuthProviderProps {
  children: ReactNode
  apiBaseUrl?: string
  firebaseConfig?: FirebaseConfig
}

// ─── Provider ─────────────────────────────────────────────────────

export function VendorAuthProvider({ children, apiBaseUrl, firebaseConfig }: VendorAuthProviderProps) {
  const [vendor, setVendor] = useState<VendorProfile | null>(null)
  const [status, setStatus] = useState<AuthStatus>('idle')
  const [step, setStep] = useState<AuthStep>('idle')
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  const [pendingPhone, setPendingPhone] = useState<string | null>(null)
  const [resetToken, setResetToken] = useState<string | null>(null)
  const initRef = useRef(false)

  // Configure API
  useEffect(() => {
    if (apiBaseUrl) {
      configureApi({
        baseUrl: apiBaseUrl,
        onUnauthorized: () => {
          setVendor(null)
          setStatus('unauthenticated')
          setStep('idle')
          tokenManager.clear()
        },
      })
    }
  }, [apiBaseUrl])

  // Initialize Firebase. Tenants without Firebase configured leave
  // firebaseEnabled=false; UI hides Google/Apple/Phone login. Email/password
  // + email OTP via Resend remain available.
  const [firebaseEnabled, setFirebaseEnabled] = useState(false)
  useEffect(() => {
    if (!firebaseConfig) {
      setFirebaseEnabled(false)
      return
    }
    setFirebaseEnabled(initFirebase(firebaseConfig))
  }, [firebaseConfig])

  // ─── Initialize: restore session ────────────────────────────────

  useEffect(() => {
    if (initRef.current) return
    initRef.current = true

    async function init() {
      if (!tokenManager.hasTokens()) {
        setStatus('unauthenticated')
        return
      }
      try {
        const response = await vendorAuthApi.me()
        const user = await resolveAuthenticatedVendor((response as { user?: unknown; vendor?: unknown }).user ?? (response as { vendor?: unknown }).vendor ?? response)
        setVendor(user)
        setStatus('authenticated')
        setStep(getNextStep(user))
      } catch {
        tokenManager.clear()
        setStatus('unauthenticated')
      }
    }
    init()
  }, [])

  // ─── Background vendor sync ────────────────────────────────────
  // Mobile parity: business_splash_controller.dart `_backgroundSync` —
  // re-fetch the vendor profile periodically while authenticated so that
  // status changes (business approved/rejected, account deactivated)
  // surface without forcing a full reload.
  useEffect(() => {
    if (status !== 'authenticated') return
    const interval = setInterval(() => {
      vendorAuthApi
        .me()
        .then(async (response) => {
          const fresh = await resolveAuthenticatedVendor(
            (response as { user?: unknown; vendor?: unknown }).user ??
              (response as { vendor?: unknown }).vendor ??
              response,
          )
          setVendor(fresh)
          setStep(getNextStep(fresh))
        })
        .catch(() => {
          // Silent — refresh interceptor + onUnauthorized handle real auth failure
        })
    }, 60_000)
    return () => clearInterval(interval)
  }, [status])

  // ─── POST /vendor/auth/register ────────────────────────────────

  const register = useCallback(async (data: VendorRegisterRequest) => {
    setStatus('loading')
    try {
      const res = await vendorAuthApi.register(data)
      // Backend returns tokens directly when the tenant has no Resend
      // (no email OTP gate). Otherwise it returns {email, requiresOtp:true}.
      // User object lives under `vendor` for BUSINESS role; legacy deploys
      // used `user`. Accept both.
      const r = res as { token?: string; refreshToken?: string; user?: unknown; vendor?: unknown }
      const userObj = r.vendor ?? r.user
      if (r.token && r.refreshToken && userObj) {
        tokenManager.set(r.token, r.refreshToken)
        const user = await resolveAuthenticatedVendor(userObj)
        setVendor(user)
        setStep(getNextStep(user))
        setStatus('authenticated')
      } else {
        setPendingEmail(res.email)
        setStep('otp_verification')
        setStatus('unauthenticated')
      }
    } catch (error) {
      setStatus('unauthenticated')
      throw error
    }
  }, [])

  // ─── POST /vendor/auth/login ───────────────────────────────────

  const login = useCallback(async (data: VendorLoginRequest) => {
    setStatus('loading')
    try {
      const res = await vendorAuthApi.login(data)
      // Backend returns tokens + user directly when the account is already
      // email-verified (OTP at login is a one-time gate, not 2FA on every
      // session). Otherwise it returns {email, requiresOtp:true} and we
      // route to the OTP-entry view.
      // The user object key is `vendor` for BUSINESS role (see backend
      // buildUserResponse); legacy deploys returned `user`. Accept both.
      const tokens = (res as { token?: string; refreshToken?: string })
      const userObj = (res as { user?: unknown; vendor?: unknown }).vendor
        ?? (res as { user?: unknown }).user
      if (tokens.token && tokens.refreshToken && userObj) {
        tokenManager.set(tokens.token, tokens.refreshToken)
        const user = await resolveAuthenticatedVendor(userObj)
        setVendor(user)
        setStep(getNextStep(user))
        setStatus('authenticated')
      } else {
        setPendingEmail(res.email)
        setStep('otp_verification')
        setStatus('unauthenticated')
      }
    } catch (error) {
      setStatus('unauthenticated')
      throw error
    }
  }, [])

  // ─── POST /vendor/auth/social-login (google) ──────────────────

  const loginWithGoogle = useCallback(async () => {
    setStatus('loading')
    try {
      const result = await signInWithGoogle()
      const res = await vendorAuthApi.socialLogin({
        token: result.idToken,
        provider: 'google',
        ...getDeviceInfo(),
      })
      tokenManager.set(res.token, res.refreshToken)
      // Backend returns the user object under `vendor` (buildUserResponse
      // returns key: 'vendor' for the BUSINESS role); older deploys may
      // have returned `user`. Accept both.
      const raw = (res as { user?: unknown; vendor?: unknown }).vendor
        ?? (res as { user?: unknown }).user
      const user = await resolveAuthenticatedVendor(raw)
      setVendor(user)
      setStep(getNextStep(user))
      setStatus('authenticated')
    } catch (error) {
      setStatus('unauthenticated')
      if (error instanceof Error && error.message === 'POPUP_CLOSED') return
      throw error
    }
  }, [])

  // ─── POST /vendor/auth/social-login (apple) ──────────────────

  const loginWithApple = useCallback(async () => {
    setStatus('loading')
    try {
      const result = await signInWithApple()
      const res = await vendorAuthApi.socialLogin({
        token: result.idToken,
        provider: 'apple',
        ...getDeviceInfo(),
      })
      tokenManager.set(res.token, res.refreshToken)
      const raw = (res as { user?: unknown; vendor?: unknown }).vendor
        ?? (res as { user?: unknown }).user
      const user = await resolveAuthenticatedVendor(raw)
      setVendor(user)
      setStep(getNextStep(user))
      setStatus('authenticated')
    } catch (error) {
      setStatus('unauthenticated')
      if (error instanceof Error && error.message === 'POPUP_CLOSED') return
      throw error
    }
  }, [])

  // ─── Phone login: Step 1 — Send Firebase OTP ───────────────────

  const sendPhoneLoginOtp = useCallback(async (phone: string, recaptchaId: string) => {
    setStatus('loading')
    try {
      await sendPhoneOtp(phone, recaptchaId)
      setPendingPhone(phone)
      setStep('otp_verification')
      setStatus('unauthenticated')
    } catch (error) {
      setStatus('unauthenticated')
      throw error
    }
  }, [])

  // ─── Phone login: Step 2 — Verify OTP → social-login ──────────

  const verifyPhoneLoginOtp = useCallback(async (otp: string) => {
    setStatus('loading')
    try {
      const result = await verifyPhoneOtp(otp)
      const res = await vendorAuthApi.socialLogin({
        token: result.idToken,
        provider: 'phone',
        ...getDeviceInfo(),
      })
      tokenManager.set(res.token, res.refreshToken)
      const raw = (res as { user?: unknown; vendor?: unknown }).vendor
        ?? (res as { user?: unknown }).user
      const user = await resolveAuthenticatedVendor(raw)
      setVendor(user)
      setPendingPhone(null)
      setStep(getNextStep(user))
      setStatus('authenticated')
    } catch (error) {
      setStatus('unauthenticated')
      throw error
    }
  }, [])

  // ─── PUT /vendor/auth/complete-profile ─────────────────────────

  const completeProfile = useCallback(async (data: CompleteProfileRequest) => {
    const res = await vendorAuthApi.completeProfile(data)
    const user = await resolveAuthenticatedVendor((res as { user?: unknown; vendor?: unknown }).user ?? (res as { vendor?: unknown }).vendor ?? res)
    setVendor(user)
    setStep(getNextStep(user))
  }, [])

  // ─── POST /vendor/auth/verify-otp ─────────────────────────────

  const verifyOtp = useCallback(async (otp: string) => {
    if (!pendingEmail) throw new Error('No pending email for OTP verification')
    // Backend's verifyOtp creates a session, so device info is required
    // (mobile parity — same payload shape as socialLogin).
    const res = await vendorAuthApi.verifyOtp({
      email: pendingEmail,
      otp,
      ...getDeviceInfo(),
    })
    tokenManager.set(res.token, res.refreshToken)
    // Backend returns the user object under `vendor` for BUSINESS role;
    // older deploys returned `user`. Accept both, normalize via resolver
    // (handles {name} vs {fullName} key mismatch too).
    const raw = (res as { user?: unknown; vendor?: unknown }).vendor
      ?? (res as { user?: unknown }).user
    const user = await resolveAuthenticatedVendor(raw)
    setVendor(user)
    setPendingEmail(null)
    setStep(getNextStep(user))
    setStatus('authenticated')
  }, [pendingEmail])

  // ─── POST /vendor/auth/resend-otp ─────────────────────────────

  const resendOtp = useCallback(async () => {
    if (!pendingEmail) throw new Error('No pending email for resend')
    await vendorAuthApi.resendOtp({ email: pendingEmail })
  }, [pendingEmail])

  // ─── POST /vendor/auth/logout ─────────────────────────────────

  const logout = useCallback(async () => {
    const refreshToken = tokenManager.getRefreshToken()
    try {
      await Promise.all([
        vendorAuthApi.logout(refreshToken ? { refreshToken } : undefined).catch(() => {}),
        signOutFirebase().catch(() => {}),
      ])
    } finally {
      tokenManager.clear()
      setVendor(null)
      setStatus('unauthenticated')
      setStep('idle')
      setPendingEmail(null)
      setPendingPhone(null)
      setResetToken(null)
    }
  }, [])

  // ─── POST /vendor/auth/forgot-password ─────────────────────────

  const forgotPassword = useCallback(async (email: string) => {
    await vendorAuthApi.forgotPassword({ email })
    setPendingEmail(email)
    setStep('forgot_password_otp')
  }, [])

  // ─── POST /vendor/auth/forgot-password/verify ──────────────────

  const verifyForgotPasswordOtp = useCallback(async (otp: string) => {
    if (!pendingEmail) throw new Error('No pending email')
    const res = await vendorAuthApi.forgotPasswordVerify({ email: pendingEmail, otp })
    setResetToken(res.token)
    setStep('reset_password')
  }, [pendingEmail])

  // ─── POST /vendor/auth/reset-password ──────────────────────────

  const resetPasswordFn = useCallback(async (newPassword: string) => {
    if (!resetToken) throw new Error('No reset token')
    await vendorAuthApi.resetPassword({ newPassword }, resetToken)
    setResetToken(null)
    setPendingEmail(null)
    setStep('idle')
  }, [resetToken])

  // ─── POST /vendor/auth/change-password ─────────────────────────

  const changePassword = useCallback(async (data: ChangePasswordRequest) => {
    await vendorAuthApi.changePassword(data)
  }, [])

  // ─── PUT /vendor/auth/profile ──────────────────────────────────

  const updateProfile = useCallback(async (data: UpdateProfileRequest): Promise<VendorProfile> => {
    const res = await vendorAuthApi.updateProfile(data)
    const user = await resolveAuthenticatedVendor((res as { user?: unknown; vendor?: unknown }).user ?? (res as { vendor?: unknown }).vendor ?? res)
    setVendor(user)
    return user
  }, [])

  // ─── GET /vendor/auth/me ───────────────────────────────────────

  const refreshVendor = useCallback(async () => {
    try {
      const res = await vendorAuthApi.me()
      const user = await resolveAuthenticatedVendor((res as { user?: unknown; vendor?: unknown }).user ?? (res as { vendor?: unknown }).vendor ?? res)
      setVendor(user)
    } catch { /* silently fail */ }
  }, [])

  // ─── POST /vendor/auth/check-availability ──────────────────────

  const checkAvailability = useCallback(async (email?: string, phone?: string) => {
    return vendorAuthApi.checkAvailability({ email, phone })
  }, [])

  // ─── Context Value ─────────────────────────────────────────────

  const value = useMemo<VendorAuthContextValue>(
    () => ({
      vendor, status, step, pendingEmail, pendingPhone, resetToken,
      isAuthenticated: status === 'authenticated',
      isLoading: status === 'loading' || status === 'idle',
      firebaseEnabled,
      register, login, loginWithGoogle, loginWithApple, sendPhoneLoginOtp, verifyPhoneLoginOtp,
      completeProfile, verifyOtp, resendOtp, logout,
      forgotPassword, verifyForgotPasswordOtp, resetPassword: resetPasswordFn, changePassword,
      updateProfile, refreshVendor, checkAvailability, setStep,
    }),
    [
      vendor, status, step, pendingEmail, pendingPhone, resetToken, firebaseEnabled,
      register, login, loginWithGoogle, loginWithApple, sendPhoneLoginOtp, verifyPhoneLoginOtp,
      completeProfile, verifyOtp, resendOtp, logout,
      forgotPassword, verifyForgotPasswordOtp, resetPasswordFn, changePassword,
      updateProfile, refreshVendor, checkAvailability,
    ],
  )

  return <VendorAuthContext.Provider value={value}>{children}</VendorAuthContext.Provider>
}
